import type { ConfidenceLevel, PlanV3 } from "./types";

interface RenameRouteGroupInPlanArgs {
  plan: PlanV3;
  oldGroupLabel: string;
  newGroupLabel: string;
  cableTypeStatus: ConfidenceLevel;
  routeTypeId: number;
}

export const renameRouteGroupInPlan = ({
  plan,
  oldGroupLabel,
  newGroupLabel,
  cableTypeStatus,
  routeTypeId,
}: RenameRouteGroupInPlanArgs): PlanV3 => {
  if (oldGroupLabel === newGroupLabel) return plan;

  const sourceGroup = plan.routeGroups.find(
    (group) => group.label === oldGroupLabel,
  );
  if (!sourceGroup) return plan;

  const targetGroup = plan.routeGroups.find(
    (group) => group.label === newGroupLabel,
  );

  // A route mirrors its group's type, so a merge has to repoint it off the deleted type.
  const movedRoutes = sourceGroup.routes.map((route) => ({
    ...route,
    cable_type: newGroupLabel,
    route_type_id: routeTypeId,
    cable_type_status: cableTypeStatus,
  }));

  if (targetGroup) {
    const mergedRoutes = [...targetGroup.routes, ...movedRoutes];
    const mergedMeters = mergedRoutes.reduce(
      (sum, route) => sum + (Number(route.meters) || 0),
      0,
    );

    const routeGroups = plan.routeGroups
      .filter((group) => group.label !== oldGroupLabel)
      .map((group) => {
        if (group.label !== newGroupLabel) return group;
        return {
          ...group,
          // A merge deletes one of the two types, so trust the server's id over the local one.
          route_type_id: routeTypeId,
          routes: mergedRoutes,
          meters: mergedMeters,
          cableTypeStatus,
        };
      });

    return { ...plan, routeGroups };
  }

  const routeGroups = plan.routeGroups.map((group) => {
    if (group.label !== oldGroupLabel) return group;
    return {
      ...group,
      route_type_id: routeTypeId,
      label: newGroupLabel,
      routes: movedRoutes,
      cableTypeStatus,
    };
  });

  return { ...plan, routeGroups };
};
